import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import Swal from "sweetalert2";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import {
  ChargeSyncPreviewAction,
  ChargeSyncApplyAction,
} from "../../../Redux/Actions/PricingAction";
import {
  BRAND_BLUE,
  pricingBodyRowSx,
  pricingTableContainerSx,
  pricingTableHeadSx,
} from "../shared/pricingHelpers";

const ACTION_STYLES: Record<string, { bg: string; fg: string; border: string }> = {
  create: { bg: "#e7f7ee", fg: "#0a7d3e", border: "#abe2c4" },
  status_update: { bg: "#e8f0fe", fg: "#0445a3", border: "#b6cdf7" },
  supersede: { bg: "#fef3e6", fg: "#b54708", border: "#f5c98a" },
  unchanged: { bg: "#f2f4f7", fg: "#667085", border: "#e0e3ea" },
};

const ActionChip: React.FC<{ action: string }> = ({ action }) => {
  const s = ACTION_STYLES[action] || ACTION_STYLES.unchanged;
  return (
    <Chip
      size="small"
      label={action}
      sx={{
        fontWeight: 600,
        fontSize: 11,
        height: 22,
        color: s.fg,
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    />
  );
};

const SummaryCard: React.FC<{ label: string; value: number | string; accent?: string }> = ({
  label,
  value,
  accent,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 140,
      p: 2,
      borderRadius: 2,
      border: "1px solid #e6e9f0",
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
    }}
  >
    <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: accent || "#101828" }}>
      {value}
    </Typography>
  </Box>
);

const ChargeSync: React.FC = () => {
  const dispatch = useDispatch<any>();
  const preview = useSelector((state: any) => state.ChargeSyncPreviewData || {});
  const apply = useSelector((state: any) => state.ChargeSyncApplyData || {});

  const [hasPreviewed, setHasPreviewed] = useState(false);

  const data = preview?.data;
  const plan: any[] = Array.isArray(data?.plan) ? data.plan : [];
  const loading = Boolean(preview?.loading || apply?.loading);

  const runPreview = async () => {
    const res = await dispatch(ChargeSyncPreviewAction());
    if (res?.status === true) setHasPreviewed(true);
  };

  const runApply = () => {
    Swal.fire({
      title: "Apply charge sync?",
      text: "This writes feature-catalog pricing into MasterServiceCharge (create / supersede / status-update) and busts the cost cache.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: BRAND_BLUE,
      cancelButtonColor: "#667085",
      confirmButtonText: "Apply sync",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const res = await dispatch(ChargeSyncApplyAction());
        if (res?.status === true) runPreview();
      }
    });
  };

  const changes = data?.changes ?? 0;

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Charge Sync"
            breadcrumbs={[
              { label: "Pricing", path: "/pricing/charge-sync", isActive: false },
              { label: "Charge Sync", path: "/pricing/charge-sync", isActive: true },
            ]}
          />
        </div>

        <Box
          sx={{
            m: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#101828" }}>
              Charge Sync
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.5 }}>
              Preview and apply the feature-catalog → MasterServiceCharge sync.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={runPreview}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<PublishRoundedIcon />}
              onClick={runApply}
              disabled={!hasPreviewed || changes === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: BRAND_BLUE,
                "&:hover": { backgroundColor: "#0445a3" },
              }}
            >
              Apply Sync
            </Button>
          </Box>
        </Box>

        {hasPreviewed && (
          <Box sx={{ mx: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <SummaryCard label="Total evaluated" value={data?.total ?? 0} />
            <SummaryCard
              label="Pending changes"
              value={changes}
              accent={changes > 0 ? "#b54708" : "#0a7d3e"}
            />
          </Box>
        )}

        <Box sx={{ mx: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={pricingTableContainerSx}>
            <Table aria-label="Charge Sync Plan">
              <TableHead sx={pricingTableHeadSx}>
                <TableRow>
                  <TableCell>Feature Key</TableCell>
                  <TableCell>Action Event</TableCell>
                  <TableCell>Unit Type</TableCell>
                  <TableCell align="right">Current → Target Credits</TableCell>
                  <TableCell>Current → Target Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.length > 0 ? (
                  plan.map((p: any, idx: number) => (
                    <TableRow key={idx} sx={pricingBodyRowSx}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{p.feature_key}</TableCell>
                      <TableCell>{p.action_event ?? "—"}</TableCell>
                      <TableCell>{p.unit_type ?? "—"}</TableCell>
                      <TableCell align="right">
                        {p.current_credit_charge ?? "—"} → {p.target_credit_charge}
                      </TableCell>
                      <TableCell>
                        {(p.current_status ?? "—") + " → " + (p.target_status ?? "—")}
                      </TableCell>
                      <TableCell align="center">
                        <ActionChip action={p.action} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#667085" }}>
                      {hasPreviewed
                        ? "Nothing to sync — catalog and charges are in step."
                        : "Click “Preview” to compute the sync plan."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
      <ToastDefault />
    </>
  );
};

export default ChargeSync;
